/* Hanan Bakri - films behind the bands below the fold.
   ---------------------------------------------------------------------------
   snippets/hb-plate.liquid emits a <video data-hb-plate-video> with
   preload="none" and NO autoplay attribute. This file is the other half: it
   fetches and plays a film only while its band is near the viewport, and
   pauses it the moment the band leaves.

   Why not just `autoplay`. Two films below the fold with autoplay start
   downloading and decoding on load, on a page that already carries a hero
   film and ~110 catalogue photographs. The reader is four screens away and
   pays for both. With preload="none" the poster - a frame from the film
   itself - is all that ships until the band is actually coming.

   Why pause on exit. A muted looping video off-screen still decodes every
   frame, which on a phone is battery and heat for something nobody is
   looking at. Pausing is the whole reason this is not a one-line CSS trick.

   Under prefers-reduced-motion nothing is ever fetched or played: the poster
   stands, which is a photograph, which is what the band needs anyway. The
   listener is live, so a reader who turns the preference on mid-visit gets
   the films stopped rather than having to reload.

   If this file never runs, the poster still renders. That is the fallback. */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Chased one screen ahead of the reader, so the first frames have landed
     by the time the band is actually in view. Below the fold this is the
     whole budget: nothing is fetched until the band is one viewport away. */
  var MARGIN = '100% 0px';

  var seen = [];

  /* iOS belt and braces, same as the hero's.

     Even with muted + playsinline + a MIME Safari accepts, an autoplay can
     still be refused: Low Power Mode blocks it outright, and Safari sometimes
     declines a video whose muted state it decides was set too late. Setting
     the PROPERTY, not only the attribute, covers the second case. If it is
     still refused nothing breaks - the poster is a frame from the film. */
  function play(v) {
    if (reduce.matches) return;
    v.muted = true;
    v.defaultMuted = true;
    /* preload="none" means the first play() is also the fetch. Asking for
       metadata first lets Safari commit to the resource before the gesture
       heuristics are applied to it. */
    if (v.preload === 'none') v.preload = 'auto';
    var p = v.play();
    if (p && p.catch) p.catch(function () {});
  }

  function watch(v) {
    if (seen.indexOf(v) !== -1) return;
    seen.push(v);

    if (!('IntersectionObserver' in window)) {
      /* No observer: play it and accept the cost rather than ship a band
         with a still where every other band has a film. */
      play(v);
      return;
    }

    /* A film marked `once` has no loop attribute: it runs through and stops
       on its last frame, and must never be restarted by a reader scrolling
       back past it. Playing it a second time turns a moment into a jingle. */
    var once = v.hasAttribute('data-hb-plate-once');
    var spent = false;

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            if (once && spent) return;
            spent = true;
            play(v);
          } else if (!once && !v.paused) {
            v.pause();
          }
        });
      },
      { rootMargin: MARGIN, threshold: 0 }
    );

    io.observe(v);

    if (once) {
      /* Nothing more to watch for once it has run. */
      v.addEventListener('ended', function () {
        io.disconnect();
      });
    }
  }

  /* The hero's film is a different case: it carries `autoplay` and must be
     running before the reader does anything, so it is never observed. What it
     needs is the iOS belt and braces - Low Power Mode refuses autoplay
     outright, and Safari sometimes declines a video whose muted state it
     decides was set too late. Setting the PROPERTY covers the second; arming
     a one-shot retry on the first touch or scroll covers the first, because
     by then a gesture exists. If it is still refused the poster stands, which
     is a frame of the film. */
  function coax(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('[data-hb-door] video'), coaxOne);
  }

  /* Keep asking until it is actually running.

     THE BUG THIS REPLACES. The first version called play() once at
     DOMContentLoaded and then armed three `once` listeners. On a phone the
     hero film is 2.2MB and has no data at DOMContentLoaded, so that first
     play() rejected, and the three retries were spent on gestures that
     arrived before the file was playable. The result was exactly what it
     looked like: the film sat on its poster until the reader touched or
     scrolled, and then started.

     So the retries are driven by the MEDIA's own readiness rather than by
     the reader's. Every event that means "there is more of this file than
     there was" gets another attempt, and the listeners are not released
     until currentTime is genuinely advancing.

     The gesture and visibility retries stay, because iOS Low Power Mode
     refuses autoplay outright no matter how ready the file is, and the only
     thing that lifts that is a real user gesture. That case is not solvable
     in script; this makes sure it is the only remaining one. */
  function coaxOne(v) {
    if (reduce.matches || v.hasAttribute('data-hb-coaxed')) return;
    v.setAttribute('data-hb-coaxed', '');

    var MEDIA = ['loadedmetadata', 'loadeddata', 'canplay', 'canplaythrough', 'progress', 'suspend'];
    var GESTURE = ['touchstart', 'pointerdown', 'scroll', 'keydown'];
    var tries = 0;
    var timer = null;

    function running() {
      return !v.paused && !v.ended && v.currentTime > 0 && v.readyState > 2;
    }

    function stop() {
      MEDIA.forEach(function (e) { v.removeEventListener(e, attempt); });
      GESTURE.forEach(function (e) { window.removeEventListener(e, attempt); });
      document.removeEventListener('visibilitychange', attempt);
      if (timer) { clearInterval(timer); timer = null; }
    }

    function attempt() {
      if (reduce.matches) { stop(); return; }
      if (running()) { stop(); return; }

      /* Setting the PROPERTY, not only the attribute: Safari sometimes
         declines a video whose muted state it decides was set too late. */
      v.muted = true;
      v.defaultMuted = true;

      var p = v.play();
      if (p && p.then) {
        p.then(function () { if (running()) stop(); }).catch(function () {});
      }
    }

    MEDIA.forEach(function (e) { v.addEventListener(e, attempt, { passive: true }); });
    GESTURE.forEach(function (e) { window.addEventListener(e, attempt, { passive: true }); });
    document.addEventListener('visibilitychange', attempt);

    /* A bounded backstop for the case where no event ever fires - a cached
       file that is already complete emits nothing further. Twelve attempts
       at half a second, then it is Low Power Mode and the poster stands. */
    timer = setInterval(function () {
      tries += 1;
      if (tries > 12 || running()) { stop(); return; }
      attempt();
    }, 500);

    attempt();
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('[data-hb-plate-video]'), watch);
    coax(root);
  }

  function init() {
    scan(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* A reader who turns the preference on mid-visit should not have to
     reload to be left alone. */
  var onChange = function () {
    if (!reduce.matches) return;
    seen.forEach(function (v) {
      if (!v.paused) v.pause();
    });
  };
  if (reduce.addEventListener) {
    reduce.addEventListener('change', onChange);
  } else if (reduce.addListener) {
    reduce.addListener(onChange);
  }

  /* Theme editor: a merchant swapping the plate re-renders the section, and
     the new <video> needs its own observer. */
  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });
})();
